import { useNavigation } from '@react-navigation/native'
import { links, platform } from 'constants'
import React, { useEffect, useMemo, useRef } from 'react'

import { GridItem } from 'partials/UpspaceTabs/widgets/subcomponents/GridItem'

import { Box } from 'widgets/Box'
import { FlatList } from 'widgets/FlatList'
import { Title } from 'widgets/Title'

import { useCurrentUser } from 'hooks/useCurrentUser'

import { fetchUserByUserId } from 'utils/queries'
import commonStyles from 'utils/styles/commonStyles'

import { Post } from 'types/post'
import { Program } from 'types/program'
import { UserType } from 'types/user'

import { EmptyState } from '../EmptyState'

interface RecentWorkoutsProps {
  title?: string
  itemWidth?: number
  itemHeight?: number
  isFeedHeader?: boolean
  recentWorkouts: Post[]
  isLoadingRecentWorkouts: boolean
  focusModeProgram?: Program
}

export const RecentWorkouts = ({
  title,
  itemWidth,
  itemHeight,
  isFeedHeader = false,
  recentWorkouts,
  isLoadingRecentWorkouts,
  focusModeProgram,
}: RecentWorkoutsProps) => {
  const { data: currentUser } = useCurrentUser()
  const navigation = useNavigation()

  const filteredPosts = useRef(Array.from(recentWorkouts))

  useEffect(() => {
    const filterPosts = async () => {
      filteredPosts.current = recentWorkouts.filter(
        (p) =>
          (p.isPending === false || p.isPending === undefined) &&
          (p.isArchived === false || p.isArchived === undefined),
      )

      if (currentUser?.id) {
        if (currentUser?.blockedUserIds && currentUser?.blockedUserIds.length > 0) {
          filteredPosts.current = filteredPosts.current.filter(
            (post) => !currentUser?.blockedUserIds?.includes(post.creator.id as string),
          )
        }

        const creatorIds = filteredPosts.current.map((post) => post.creator.id as string)
        const uniqueCreatorIds = creatorIds.filter(
          (value, index, self) => self.indexOf(value) === index,
        )

        const fullCreators: UserType[] = []
        const creatorPromises = uniqueCreatorIds.map((id: string) =>
          fetchUserByUserId({
            userId: id,
          }).then((creator) => {
            if (creator) {
              fullCreators.push(creator)
            }
          }),
        )

        await Promise.all(creatorPromises)

        fullCreators.forEach((fullCreator) => {
          if (fullCreator?.blockedUserIds && fullCreator?.blockedUserIds?.length > 0) {
            if (fullCreator.blockedUserIds.indexOf(currentUser.id as string) > -1) {
              filteredPosts.current = filteredPosts.current.filter(
                (post) => post.creator.id !== fullCreator.id,
              )
            }
          }
        })
      }
    }

    if (filteredPosts.current) {
      filterPosts()
    }
  }, [recentWorkouts, isLoadingRecentWorkouts, currentUser])

  const showEmptyState = useMemo(
    () =>
      isFeedHeader &&
      focusModeProgram &&
      filteredPosts.current &&
      filteredPosts.current.length === 0,
    [focusModeProgram, isFeedHeader],
  )

  return (
    <>
      {title && <Title bold>{title}</Title>}
      {!isLoadingRecentWorkouts && (
        <Box
          testID='RecentWorkoutsRow'
          flexDirection='row'
          justifyContent='flex-start'
          width='100%'
          margin={{ top: showEmptyState ? 'lg' : undefined, bottom: 'md' }}
          padding={{ bottom: 'smd' }}
          style={platform.IS_WEB ? commonStyles.overflowScroll : undefined}
        >
          {showEmptyState ? (
            <EmptyState
              inBetweenMargin='sm'
              title={`Let's Get Started!`}
              subtitle='Build a workout to get your Group moving.'
              actionButtons={[
                {
                  title: 'Build Workout',
                  type: 'primary',
                  onPress: () => navigation.navigate(links.CREATE_WORKOUT_PAGE),
                },
              ]}
            />
          ) : (
            <FlatList<Post>
              horizontal
              data={filteredPosts.current}
              loading={isLoadingRecentWorkouts}
              renderItem={({ item: post }) => (
                <GridItem
                  key={post.id}
                  post={post}
                  isUpspaceItem
                  width={itemWidth}
                  height={itemHeight}
                />
              )}
              keyExtractor={(post) => {
                return post.id
              }}
              testID='RecentWorkoutsList'
              backgroundColor='white'
              textColor='textInverted'
              contentContainerStyle={commonStyles.flexGrow}
            />
          )}
        </Box>
      )}
    </>
  )
}
